import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Button, Tag, Progress, Typography, Space, Spin, Statistic, Row, Col,
  Descriptions, message,
} from 'antd';
import {
  ArrowLeftOutlined, ThunderboltOutlined, PlayCircleOutlined, PauseCircleOutlined,
  CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, SyncOutlined,
  EyeInvisibleOutlined, FolderAddOutlined, RobotOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

interface AutoModSession {
  id: number;
  name: string;
  dataset_id: number | null;
  status: string;
  total_images: number;
  classified_count: number;
  excluded_count: number;
  model3_used_count: number;
  routed_count: number;
  failed_count: number;
  models: { provider?: string; model1?: string; model2?: string; model3?: string; model4?: string } | null;
  prompt_id: number;
  collection_prefix?: string;
  classifier?: string;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

const AutoModerationView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<AutoModSession | null>(null);
  const [collections, setCollections] = useState<{ name: string; count: number; dataset_id: number | null }[]>([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const fetchSession = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/auto-moderation-sessions/${id}`);
      setSession(res.data);
    } catch {
      message.error('Failed to load session');
      navigate('/moderation');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchCollections = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/auto-moderation-sessions/${id}/collections`);
      setCollections(res.data);
    } catch { /* non-fatal */ }
  }, [id]);

  useEffect(() => { fetchSession(); fetchCollections(); }, [fetchSession, fetchCollections]);

  // Live progress over WebSocket (mirrors the captioning task view).
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!id) return;
    let attempts = 0;
    const connect = () => {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${process.env.REACT_APP_WS_HOST || 'localhost:3003'}/ws`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => {
        attempts = 0;
        ws.send(JSON.stringify({ action: 'subscribe', channel: `auto-moderation:${id}` }));
      };
      ws.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data);
          const { data, type } = parsed;
          if (type === 'connected') return;
          if (data?.type === 'auto-mod-progress') {
            setSession(prev => prev ? {
              ...prev,
              classified_count: data.classified,
              excluded_count: data.excluded,
              model3_used_count: data.model3_used,
              routed_count: data.routed,
              failed_count: data.failed,
            } : prev);
          }
          if (data?.type === 'task-status') {
            setSession(prev => prev ? { ...prev, status: data.status } : prev);
            fetchSession();
            fetchCollections();
          }
        } catch { /* ignore */ }
      };
      ws.onclose = (e) => {
        wsRef.current = null;
        if (e.code !== 1000 && attempts < 5) {
          attempts++;
          reconnectRef.current = setTimeout(connect, Math.min(1000 * 2 ** attempts, 10000));
        }
      };
    };
    connect();
    return () => {
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) {
        if (wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ action: 'unsubscribe', channel: `auto-moderation:${id}` }));
        }
        wsRef.current.close(1000, 'unmount');
      }
    };
  }, [id, fetchSession, fetchCollections]);

  // Poll as a fallback while running (also refreshes the per-collection counts).
  useEffect(() => {
    if (session?.status !== 'running') return;
    const t = setInterval(() => { fetchSession(); fetchCollections(); }, 5000);
    return () => clearInterval(t);
  }, [session?.status, fetchSession, fetchCollections]);

  const handleStart = async () => {
    setActing(true);
    try {
      await axios.post(`${API_URL}/auto-moderation-sessions/${id}/start`);
      message.success('Auto-moderation started');
      fetchSession();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to start');
    } finally {
      setActing(false);
    }
  };

  const handlePause = async () => {
    setActing(true);
    try {
      await axios.post(`${API_URL}/auto-moderation-sessions/${id}/pause`);
      message.success('Paused');
      fetchSession();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to pause');
    } finally {
      setActing(false);
    }
  };

  const statusTag = (status: string) => {
    const cfg: Record<string, { color: string; icon: React.ReactNode }> = {
      pending: { color: 'default', icon: <ClockCircleOutlined /> },
      running: { color: 'processing', icon: <SyncOutlined spin /> },
      paused: { color: 'warning', icon: <PauseCircleOutlined /> },
      completed: { color: 'success', icon: <CheckCircleOutlined /> },
      failed: { color: 'error', icon: <CloseCircleOutlined /> },
    };
    const { color, icon } = cfg[status] || { color: 'default', icon: null };
    return <Tag color={color} icon={icon}>{status.toUpperCase()}</Tag>;
  };

  if (loading || !session) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  const processed = session.classified_count + session.failed_count;
  const percent = session.total_images > 0
    ? Math.round((processed / session.total_images) * 100) : 0;
  const models = session.models || {};

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/moderation')}
              style={{ marginBottom: 8, marginLeft: -8 }}>
              Back to Moderation
            </Button>
            <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
              <ThunderboltOutlined style={{ color: '#6366f1' }} />
              {session.name}
              {statusTag(session.status)}
            </Title>
          </div>
          <Space>
            {(session.status === 'pending' || session.status === 'paused' || session.status === 'failed') && (
              <Button type="primary" icon={<PlayCircleOutlined />} size="large"
                loading={acting} onClick={handleStart}>
                {session.status === 'pending' ? 'Start' : 'Resume'}
              </Button>
            )}
            {session.status === 'running' && (
              <Button icon={<PauseCircleOutlined />} size="large" loading={acting} onClick={handlePause}>
                Pause
              </Button>
            )}
          </Space>
        </div>
      </Card>

      <Card style={{ marginBottom: 16 }}>
        <Progress
          percent={percent}
          status={session.status === 'failed' ? 'exception' : session.status === 'running' ? 'active' : undefined}
          strokeColor={{ '0%': '#6366f1', '100%': '#52c41a' }}
        />
        <Row gutter={16} style={{ marginTop: 24 }}>
          <Col span={4}>
            <Statistic title="Total" value={session.total_images}
              prefix={<RobotOutlined style={{ color: '#6366f1' }} />} />
          </Col>
          <Col span={4}>
            <Statistic title="Classified" value={session.classified_count}
              valueStyle={{ color: '#52c41a' }} prefix={<CheckCircleOutlined />} />
          </Col>
          <Col span={4}>
            <Statistic title="Routed" value={session.routed_count}
              valueStyle={{ color: '#1890ff' }} prefix={<FolderAddOutlined />} />
          </Col>
          <Col span={4}>
            <Statistic title="Censored (excluded)" value={session.excluded_count}
              valueStyle={{ color: '#faad14' }} prefix={<EyeInvisibleOutlined />} />
          </Col>
          <Col span={4}>
            <Statistic title="Escalated to final" value={session.model3_used_count}
              valueStyle={{ color: '#722ed1' }} prefix={<SyncOutlined />} />
          </Col>
          <Col span={4}>
            <Statistic title="Failed" value={session.failed_count}
              valueStyle={{ color: '#ff4d4f' }} prefix={<CloseCircleOutlined />} />
          </Col>
        </Row>
      </Card>

      <Card
        title="Routed to collections"
        style={{ marginBottom: 16 }}
        extra={
          <Space>
            <Tag color="purple">classifier: {session.classifier || 'default'}</Tag>
            <Tag color="geekblue">label: {session.collection_prefix || 'auto'}</Tag>
            <Text type="secondary">{collections.length} collection{collections.length === 1 ? '' : 's'}</Text>
          </Space>
        }
      >
        {collections.length === 0 ? (
          <Text type="secondary">No photos routed yet.</Text>
        ) : (
          <Space wrap size={[8, 8]}>
            {collections.map(c => (
              <Button
                key={c.name}
                size="small"
                onClick={() => c.dataset_id && navigate(`/datasets/${c.dataset_id}`)}
                disabled={!c.dataset_id}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
              >
                <FolderAddOutlined />
                {c.name}
                <Tag color="blue" style={{ marginInlineEnd: 0 }}>{c.count}</Tag>
              </Button>
            ))}
          </Space>
        )}
      </Card>

      <Card title="Reasoning chain">
        <Descriptions column={1}>
          <Descriptions.Item label="1 · Classifier (classifies + explains)">
            <Tag color="blue">{models.model1 || 'default'}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="2 · Reviewer (judges #1, synthesizes)">
            <Tag color="blue">{models.model2 || 'default'}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="3 · Adjudicator (runs only on disagreement)">
            <Tag color="blue">{models.model3 || 'default'}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="4 · Final authority (only if #3 unsure)">
            <Tag color="purple">{models.model4 || 'default'}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Provider">
            <Tag>{(models.provider || 'nebius').toUpperCase()}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Created">{new Date(session.created_at).toLocaleString()}</Descriptions.Item>
          {session.completed_at && (
            <Descriptions.Item label="Completed">{new Date(session.completed_at).toLocaleString()}</Descriptions.Item>
          )}
        </Descriptions>
      </Card>
    </div>
  );
};

export default AutoModerationView;
